import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AboutSection = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: '1889',      label: t('about_stat1'), icon: 'ri-history-line' },
    { value: '€9.4 B',   label: t('about_stat2'), icon: 'ri-money-euro-circle-line' },
    { value: '33 000+',  label: t('about_stat3'), icon: 'ri-team-line' },
    { value: '50+',      label: t('about_stat4'), icon: 'ri-global-line' },
  ];

  const tags = t('about_tags').split(',');

  return (
    <section id="about" className="py-24 bg-[#0d0d0d]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className={`flex-1 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-4 py-1.5 mb-6">
              <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium">{t('about_badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {t('about_title1')}<br />
              <span className="text-[#d4af37]">{t('about_title2')}</span><br />
              {t('about_title3')}
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t('about_desc1')}
            </p>
            <p className="text-white/65 text-base leading-relaxed mb-8" style={{ fontFamily: "'Inter', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: t('about_desc2') }}
            />
            <div className="flex flex-wrap gap-3">
              {tags.map((tag) => (
                <span key={tag} className="bg-white/5 border border-white/10 text-white/60 text-xs px-4 py-1.5 rounded-full tracking-wider">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className={`flex-1 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/8e83f195b0df950850f6766aa8442b0f.jpg"
                alt="Rheinmetall production"
                className="w-full h-72 md:h-96 object-cover object-top rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-black/70 backdrop-blur-sm border border-[#d4af37]/20 rounded-xl p-4">
                  <div className="text-[#d4af37] text-2xl font-black" style={{ fontFamily: "'Oswald', sans-serif" }}>€50+ B</div>
                  <div className="text-white/70 text-sm mt-1">{t('about_img_caption')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 md:mt-16">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white/3 border border-white/8 rounded-2xl p-6 text-center transition-all duration-700 hover:border-[#d4af37]/40 hover:bg-white/5"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-[#d4af37] text-2xl`}></i>
              </div>
              <div className="text-2xl font-black text-white mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{stat.value}</div>
              <div className="text-white/50 text-xs tracking-wider uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;